import assign from 'lodash/assign';
import noop from 'lodash/noop';
import { push } from 'react-router-redux';
import { call, delay, put, race, select, take } from 'redux-saga/effects';

import * as SimstreamActions from '../actions';
import SimStreamViolation from '../components/ViolationModal/SimStreamViolation';
import * as SimstreamConstants from '../constants';
import * as D from '../deps';
import * as SimstreamUtils from '../utils';

const log = D.Logger.child({ component: 'domains/simstream/saga/workers' });
const spawn = D.taskManagerSpawnFactory(SimstreamConstants.DOMAIN_NAME);

// Function for when user wants to keep listening
function* letMeListen(action) {
  try {
    const shouldStartStation = action.data && action.data.shouldStartStation;
    const sourceId = yield select(D.getInDomain('playback.utils.getCurrentPlaylist'));
    const isAPS = yield select(
      D.getInDomain('playback.utils.isPlaylistModelTypeAPSItem'),
      sourceId,
    );
    // keep the violation but mark it
    // as stale so that it can be used for analytics in future actions
    yield put(SimstreamActions.updateStreamViolationIsStale());

    yield put(D.Actions.SamplePlayer.endSample());

    yield put(D.getInDomain('analytics.actions.ttmNonAdInterruption', noop)());

    // shouldStartStation should usually be true, but in the case that we call this
    // from playbackIntercept middleware the start station action will already start
    // the station once it's allowed to pass
    if (shouldStartStation) {
      if (isAPS) {
        yield put(
          D.getInDomain('playback.actions.playCurrentItemForSource')({
            sourceId,
            forceActive: true,
          }),
        );
      } else {
        yield put(D.Actions.UseCases.startStation({ stationId: sourceId }));
      }
    }
    if (!isAPS) {
      yield put(D.Actions.Playback.logTrackResumed(true));
    }
  } finally {
    yield put(D.Actions.Modal.closeModal());
  }
}

// Function for when user wants to let other person listen
function* letThemListen() {
  try {
    // keep the violation but mark it
    // as stale so that it can be used for analytics in future actions
    yield put(SimstreamActions.updateStreamViolationIsStale());
    const isListenerPremium = yield select(D.Utils.Listener.hasPremiumFeatures);
    if (isListenerPremium) {
      yield put(push(D.Utils.Route.getMyMusicPath()));
    } else {
      yield put(push(D.Utils.Route.getMyStationsPath()));
    }
  } finally {
    yield put(D.Actions.SamplePlayer.endSample());
    yield put(D.Actions.Modal.closeModal());
  }
}

function mapStreamViolationErrorDataToProps(errorData) {
  if (!errorData) {
    return {};
  }
  return {
    activeDeviceImageId: errorData.get('activeDeviceImageId'),
    dialogueHeader: errorData.get('dialogueHeader'),
    dialoguePrompt: errorData.get('dialoguePrompt'),
  };
}

// Actually trigger the modal to be shown
export function* openSimStreamModal(action) {
  yield call(playSimStreamWarning);
  const shouldStartStation = action.data.shouldStartStation;
  const streamViolationErrorData = yield select(SimstreamUtils.getStreamViolationErrorData);
  const simStreamViolationProps = mapStreamViolationErrorDataToProps(streamViolationErrorData);
  simStreamViolationProps.shouldStartStation = shouldStartStation;

  const modalConfig = assign(
    {
      modalType: 'ModalCustom',
      body: SimStreamViolation,
    },
    simStreamViolationProps,
  );
  yield put(
    D.Actions.Modal.openModal(modalConfig, null, D.Constants.Overlay.SimStreamModalSubtype),
  );
  // Kind of like a channel between modal component back to here.
  // Control flow blocks bubbling up until the user takes an action on sim stream violation
  const streamDecision = yield race({
    letMeListen: take(SimstreamConstants.ActionKeys.LET_ME_LISTEN),
    letThemListen: take(SimstreamConstants.ActionKeys.LET_THEM_LISTEN),
  });
  if (streamDecision.letThemListen) {
    throw new D.Errors.SimStreamError('User opted for Let Them Listen');
  } else if (streamDecision.letMeListen) {
    log.warn('SimStreamError: User opted for Let Me Listen');
  }
}

// Trigger a sound warning if their tab is backgrounded
export function* playSimStreamWarning() {
  if (!D.Utils.PageVisibility.isHidden()) {
    return;
  }
  const streamViolationErrorData = yield select(SimstreamUtils.getStreamViolationErrorData);
  const audioWarningTrack = streamViolationErrorData.get('audioWarningTrack');
  // TODO: WEB-8882 currently uses the sample playback mechanism. should be refactored in the future
  yield put(D.Actions.SamplePlayer.playSample(audioWarningTrack.get('audioURL'), false));
}

export function* onPlayTrack() {
  if (yield select(SimstreamUtils.isStreamViolationStale)) {
    yield put(SimstreamActions.clearStreamViolation());
  }
}

// Periodically make sure the stream violation is still valid from whence it
// was determined up to the modal showing up
export function* checkStreamViolation() {
  const shouldExpireStreamViolation =
    !(yield select(SimstreamUtils.isStreamViolationStale)) &&
    (yield select(SimstreamUtils.isStreamViolationErrorValid));

  // mark any outstanding sim stream violation as stale
  if (shouldExpireStreamViolation) {
    yield put(SimstreamActions.updateStreamViolationIsStale());
  }
}

export function* insertNewSimStreamViolation(action) {
  yield put(SimstreamActions.createStreamViolation(action.data));
  // For the scenario of pausing and playing a track within the time between stream violation and modal popping up
  if (action.data.doUpdate) {
    yield spawn(updateSimStreamTimeout, action.data.errorData);
  }
}

// Ensures the sim stream modal shows up after a certain amount of time after
// a violation was determined
export function* updateSimStreamTimeout(errorData) {
  const delayTime =
    ((errorData && errorData.dialogueDelay) ||
      SimstreamConstants.StreamViolationDefaults.DIALOGUE_DELAY) * 1000;
  const racer = yield race({
    timeout: delay(delayTime),
    cancel: take([
      SimstreamConstants.ActionKeys.CREATE_STREAM_VIOLATION,
      SimstreamConstants.ActionKeys.CLEAR_STREAM_VIOLATION,
    ]),
  });

  if (!racer.timeout || !(yield select(D.getInDomain('playback.utils.isAudioPlaying', false)))) {
    return;
  }

  const isViolationStale = yield select(SimstreamUtils.isStreamViolationStale);
  const isViolationValid = yield select(SimstreamUtils.isStreamViolationErrorValid);
  if (isViolationStale || !isViolationValid) {
    return;
  }
  try {
    yield call(D.putAndWait, D.getInDomain('playback.actions.promptStreamViolation')());
    yield put(D.Actions.Playback.intentPlayTrack({ dontDispatchPlaybackResumed: true }));
  } catch (e) {
    if (e instanceof D.Errors.SimStreamError) {
      log.trace('User rejected playing the stream', JSON.stringify(e));
    }
  }
}

export default {
  playSimStreamWarning,
  openSimStreamModal,
  insertNewSimStreamViolation,
  checkStreamViolation,
  letMeListen,
  letThemListen,
  onPlayTrack,
};
